import { RequirementsList, Stack, Text, showToast } from "@certn/cascadia";
import { FileUpload } from "@certn/cascadia-react";
import {
  useClientPortalAPI,
  useClientPortalSubmit,
} from "@certn/api/src/useClientPortalAPI";
import { useTranslation } from "react-i18next";
import { useUser } from "../../hooks/useUser";
import { v4 as uuidv4 } from "uuid";
import { useState } from "react";
import { FileInfo } from "@certn/cascadia-react/components/upload/assets/utils";
import { CascadiaRequirement } from "@certn/cascadia/components/requirements-list/types";
import isEmpty from "lodash/isEmpty";
import { dataToFile } from "@certn/application";
import { getCvReviewChecksForExtraction, CVReviewChecks } from "./utils";
import { Schemas } from "../../types";

type CVReviewUploadProps = {
  customerGroupId: string;
  initialCvReviewId?: string | null;
  onCVReviewChange?: (cvReviewId?: string | null) => void;
  setDisableCompletion: (d: boolean) => void;
  checks: CVReviewChecks[];
};

export const CVReviewUpload = ({
  customerGroupId,
  initialCvReviewId,
  onCVReviewChange,
  setDisableCompletion,
  checks,
}: CVReviewUploadProps) => {
  const { t } = useTranslation();
  const { details: userDetails } = useUser();
  const auth0Name = userDetails?.client_account?.auth0_name || uuidv4();
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [uploading, setUploading] = useState(false);

  const cvReviewFileTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const { submit: createCVReview } = useClientPortalSubmit(
    "/api/internal/client-portal/cv-review/create/"
  );

  const { submit: retryExtraction } = useClientPortalSubmit(
    `/api/internal/client-portal/cv-review/${initialCvReviewId}/retry-extraction/`
  );

  const { submit: deleteCVReview } = useClientPortalSubmit(
    `/api/internal/client-portal/cv-review/${initialCvReviewId}/delete/`,
    { showToastOnError: true }
  );

  const setCvReviewDataOnLoad = (input: {
    data?: Schemas["CVReviewResponse"] | null;
    files: FileInfo[];
  }) => {
    if (input.data && input.files.length === 0) {
      const file_key = input.data.file_key;
      const file = dataToFile(file_key);

      if (file) {
        setFiles([file]);
      }
    }
  };

  const { data: cvReview, mutate: mutateCvReview } = useClientPortalAPI(
    initialCvReviewId
      ? `/api/internal/client-portal/cv-review/${initialCvReviewId}/`
      : null,
    undefined,
    {
      refreshInterval: () => {
        setDisableCompletion(true);

        if (!initialCvReviewId) return 0;

        const status = cvReview?.status;

        if (
          status === "PENDING_REVIEW" ||
          status === "CONFIRMED" ||
          status === "EXTRACTION_FAILED"
        ) {
          if (status !== "EXTRACTION_FAILED") {
            setDisableCompletion(false);
          }

          setCvReviewDataOnLoad({
            data: cvReview,
            files,
          });

          return 0;
        }

        return 2000;
      },
      onSuccess: (data: Schemas["CVReviewResponse"] | null, _key, _config) => {
        if (
          data?.status === "PENDING_REVIEW" ||
          data?.status === "EXTRACTION_FAILED"
        ) {
          onCVReviewChange?.(data?.id);
        }

        setCvReviewDataOnLoad({
          data,
          files,
        });
      },
    }
  );

  const onFileUploaded = async (fileKey: string) => {
    await createCVReview({
      file_key: fileKey,
      customer_group_id: customerGroupId,
      checks: getCvReviewChecksForExtraction(checks),
    }).then((data?: Schemas["CVReviewResponse"]) => {
      onCVReviewChange?.(data?.id);
    });
  };

  const onRemove = async () => {
    if (initialCvReviewId) {
      await deleteCVReview()
        .then(() => {
          setFiles([]);
          onCVReviewChange?.(null);
          setDisableCompletion(true);
        })
        .catch(() => {
          showToast(
            t("An error occurred while removing file. Please try again later."),
            { type: "error" }
          );
        });
    } else {
      setFiles([]);
      setDisableCompletion(true);
    }
  };

  const handleRetryExtraction = async () => {
    if (!initialCvReviewId) return;

    await retryExtraction({
      checks: getCvReviewChecksForExtraction(checks),
    }).then(() => {
      mutateCvReview();
    });
  };

  const getExtractionLabel = (checks: CVReviewChecks[]): string => {
    const labels = [
      {
        keys: ["EXPERIENCE_VERIFICATIONS_1"],
        label: "education and employment",
      },
      {
        keys: ["EMPLOYMENT_VERIFICATIONS_1"],
        label: "employment",
      },
      { keys: ["EDUCATION_VERIFICATIONS_1"], label: "education" },
    ];

    const active = labels
      .filter(({ keys }) =>
        checks.some(
          (c) =>
            keys.includes(c.key) &&
            c.check_arguments?.include_cv_review === true
        )
      )
      .map(({ label }) => label);

    return t("Extracting {{types}} information...", {
      types: active.join(" and ") || "CV",
    });
  };

  const getStatusList = (): CascadiaRequirement[] => {
    if (isEmpty(files) && !initialCvReviewId) return [];

    const list: CascadiaRequirement[] = [];

    if (uploading) {
      list.push({ text: t("Upload in progress..."), style: "normal" });
    } else if (files.length > 0) {
      list.push({ text: t("Upload complete"), style: "success" });
    }

    const status = cvReview?.status;

    if (status === "EXTRACTING") {
      list.push({
        text: getExtractionLabel(checks),
        style: "normal",
      });
    } else if (status === "EXTRACTION_FAILED") {
      list.push({
        text: t("Unable to extract CV information."),
        style: "error",
        link: {
          text: t("Retry"),
          onClick: handleRetryExtraction,
        },
      });
    } else if (status === "PENDING_REVIEW" || status === "CONFIRMED") {
      list.push({
        text: t("CV information ready for review on the next step"),
        style: "success",
      });
    }

    return list;
  };

  return (
    <Stack gap="space-4">
      <Stack gap="space-1">
        <Text style="input/label">{t("CV / resume upload")}</Text>
        <Text style="form/body-secondary" color="text-weak">
          {t("Upload the applicant's CV for comparison during screening.")}
        </Text>
      </Stack>
      <FileUpload
        orientation="horizontal"
        icon="cloud_upload"
        testID="cv-review-upload"
        bucketDirectory="cv_uploads"
        requestUrl="api/internal/client-portal/generate-presigned-url/"
        allowedFileTypes={cvReviewFileTypes}
        fileTypes={cvReviewFileTypes}
        maxSizeBytes={10000000}
        multiple={false}
        generateFileName={(file) => `${auth0Name}/${uuidv4()}-${file.name}`}
        overwriteExistingFiles
        files={files}
        onRemove={onRemove}
        onUpload={(state) => {
          if (state === "init") {
            setUploading(true);
            setDisableCompletion(true);
          } else {
            setUploading(false);
          }
        }}
        setFiles={(newFiles) => {
          if (newFiles.length > 0) {
            setFiles(newFiles);
            setUploading(false);
            const uploadedFile = newFiles[newFiles.length - 1];

            if (uploadedFile.key) {
              onFileUploaded(uploadedFile.key);
            }
          }
        }}
      />
      <RequirementsList requirements={getStatusList()} />
    </Stack>
  );
};
